const GENERATED_EMAIL_PREFIX = 'aara-';
const GENERATED_EMAIL_SUFFIX = '-collections@gmail.com';
const GENERATED_EMAIL_PATTERN =
  /^aara-(\d+)-collections@gmail\.com$/i;
const INDIAN_LOCAL_PHONE_MAX_DIGITS = 10;

export interface NormalizedPhoneIdentity {
  /** Local digits only, used for Firebase generated-email creation. */
  phone: string;
  /** Canonical Hasura `mst_users.mobile` value (10 local digits, no country prefix). */
  storedPhone: string;
  generatedEmail: string;
}

export function normalizeIndianPhone(rawPhone: string): string {
  const digits = String(rawPhone ?? '').replace(/\D/g, '');
  const local = digits.startsWith('91') && digits.length > INDIAN_LOCAL_PHONE_MAX_DIGITS
    ? digits.slice(2)
    : digits;

  if (!/^\d{10}$/.test(local)) {
    throw new Error('Please enter a valid phone number.');
  }

  return local;
}

export function sanitizeIndianPhoneInput(rawPhone: string): string {
  const digits = String(rawPhone ?? '').replace(/\D/g, '');
  const local = digits.startsWith('91') && digits.length > INDIAN_LOCAL_PHONE_MAX_DIGITS
    ? digits.slice(2)
    : digits;

  return local.slice(0, INDIAN_LOCAL_PHONE_MAX_DIGITS);
}

export function generateFirebaseEmailFromPhone(phone: string): string {
  return `${GENERATED_EMAIL_PREFIX}${phone}${GENERATED_EMAIL_SUFFIX}`;
}

export function formatIndianPhoneForStorage(phone: string): string {
  return normalizeIndianPhone(phone);
}

export function normalizePhoneIdentity(rawPhone: string): NormalizedPhoneIdentity {
  const phone = normalizeIndianPhone(rawPhone);
  return {
    phone,
    storedPhone: formatIndianPhoneForStorage(phone),
    generatedEmail: generateFirebaseEmailFromPhone(phone),
  };
}

export function isGeneratedFirebaseEmail(value: string | null | undefined): boolean {
  return GENERATED_EMAIL_PATTERN.test(String(value ?? '').trim());
}

export function phoneFromGeneratedFirebaseEmail(
  value: string | null | undefined
): string | null {
  const match = String(value ?? '').trim().match(GENERATED_EMAIL_PATTERN);
  return match?.[1] ?? null;
}

export function storedPhoneFromGeneratedFirebaseEmail(
  value: string | null | undefined
): string | null {
  const phone = phoneFromGeneratedFirebaseEmail(value);
  return phone ? formatIndianPhoneForStorage(phone) : null;
}

export function maskPhone(value: string | null | undefined): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length <= 4) return digits ? '*'.repeat(digits.length) : '';
  return `${'*'.repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
}

export function maskGeneratedEmail(value: string | null | undefined): string {
  const phone = phoneFromGeneratedFirebaseEmail(value);
  return phone ? `aara-${maskPhone(phone)}-collections@gmail.com` : '';
}
